import { ISAN_DATA } from './isan-places.data.ts';
import { cleanText } from './text.ts';

/**
 * The provinces and districts (อำเภอ) of Isan with a centre point each (owner, 22 ต.ค. 2569: "สร้างข้อมูล จังหวัด และ
 * อำเภอทั้งหมดในภาคอีสาน มาเก็บเป็นฐานข้อมูลเรา"). The data is OpenStreetMap's (© OpenStreetMap contributors, ODbL),
 * fetched once and kept in `isan-places.data.ts` — nothing is looked up online at run time.
 *
 * Used to (1) work out the drive from each district to the field (`venue_travel`, `{ระยะทางมาสนาม}`) and (2) make the
 * registration form smarter: lists to pick from, and a district typed without its province fills the province.
 */

export interface IsanPlace {
  province: string;
  /** '' = the province itself (its centre), for a customer who gave only the province. */
  district: string;
  lat: number;
  lng: number;
}

export const ISAN_PROVINCES: readonly string[] = Object.keys(ISAN_DATA);

export const ISAN_PLACES: readonly IsanPlace[] = Object.entries(ISAN_DATA).flatMap(([province, { c, d }]) => [
  { province, district: '', lat: c[0], lng: c[1] },
  ...d.map(([district, lat, lng]) => ({ province, district, lat, lng })),
]);

// "เเ" (two เ) typed for "แ" is the commonest slip in customers' messages.
const key = (s: string) => s.replace(/[\s.]/g, '').replace(/เเ/g, 'แ');

/** "อำเภอเมือง", "อ. เมือง", "เมือง" → "เมือง" (prefix and spaces gone, nothing else changed). */
export function stripDistrictPrefix(input: string | null | undefined): string {
  return cleanText(input)
    .replace(/^(?:อำเภอ|อ\.|เขต)\s*/u, '')
    .trim();
}

/** The districts of an Isan province (Thai names, as in the data), [] for any other province. */
export function isanDistricts(province: string | null | undefined): string[] {
  const p = ISAN_DATA[cleanText(province)];
  return p ? p.d.map(([name]) => name) : [];
}

/**
 * The district as kept in the data, or null when it is not an Isan district. "เมือง" alone means the province's own
 * district ("เมืองร้อยเอ็ด") when the province is known. Without a province a name must be unique across Isan
 * (only "เฉลิมพระเกียรติ" is not — it exists in นครราชสีมา and บุรีรัมย์) — then `province` tells which one it is.
 */
export function findIsanDistrict(district: string | null | undefined, province?: string | null): { province: string; district: string } | null {
  const raw = key(stripDistrictPrefix(district));
  if (!raw) return null;
  const prov = cleanText(province);
  const inProvince = (p: string) => {
    const list = ISAN_DATA[p]?.d ?? [];
    const hit = list.find(([n]) => key(n) === raw) ?? (raw === 'เมือง' ? list.find(([n]) => n === `เมือง${p}`) : undefined);
    return hit ? { province: p, district: hit[0] } : null;
  };
  if (prov && ISAN_DATA[prov]) return inProvince(prov);
  if (prov) return null;
  const all = ISAN_PROVINCES.map(inProvince).filter((x): x is { province: string; district: string } => x !== null);
  return all.length === 1 ? all[0]! : null;
}

/** The point of a place: the district's centre, else the province's, else null (not Isan / unknown). */
export function isanPlace(province: string | null | undefined, district?: string | null): IsanPlace | null {
  const hit = findIsanDistrict(district, province);
  if (hit) return ISAN_PLACES.find((p) => p.province === hit.province && p.district === hit.district) ?? null;
  const prov = cleanText(province);
  return ISAN_PLACES.find((p) => p.province === prov && p.district === '') ?? null;
}

/** Edit distance (Levenshtein) between two strings, by code point. */
function distance(a: string, b: string): number {
  const x = [...a];
  const y = [...b];
  let prev = Array.from({ length: y.length + 1 }, (_, j) => j);
  for (let i = 1; i <= x.length; i++) {
    const cur = [i];
    for (let j = 1; j <= y.length; j++) cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (x[i - 1] === y[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[y.length]!;
}

/**
 * Isan districts that look like what was typed (owner, 22 ต.ค. 2569: "ลูกค้าพิมอำเภอหรือจังหวัดผิดมาจริงๆ ระบบก็จะช่วย
 * แก้ให้ถูก"): names starting with it first, then a few spelling slips away. Inside the province when it is one of
 * ours, else across Isan. Empty when the text is already a district we know.
 */
export function suggestIsanDistricts(input: string | null | undefined, province?: string | null, limit = 4): { province: string; district: string }[] {
  const raw = key(stripDistrictPrefix(input));
  if (!raw || findIsanDistrict(input, province)) return [];
  const prov = cleanText(province);
  const pool = ISAN_PLACES.filter((p) => p.district && (!ISAN_DATA[prov] || p.province === prov));
  const allowed = Math.max(1, Math.floor([...raw].length / 3));
  return pool
    .map((p) => {
      const name = key(p.district);
      const score = name.startsWith(raw) ? 0 : name.includes(raw) ? 0.5 : distance(raw, name);
      return { p, score };
    })
    .filter((x) => x.score <= allowed)
    .sort((a, b) => a.score - b.score || a.p.district.localeCompare(b.p.district, 'th'))
    .slice(0, limit)
    .map(({ p }) => ({ province: p.province, district: p.district }));
}

/** The closest Thai province to a misspelt one ("ขอนเเก่น" → "ขอนแก่น"), or null. */
export function suggestProvince(input: string | null | undefined, provinces: readonly string[]): string | null {
  const typed = cleanText(input)
    .replace(/^(?:จังหวัด|จ\.)\s*/u, '')
    .replace(/\s+/g, '');
  if (!typed || provinces.includes(typed)) return null;
  // `key` mends "เเ" (two เ) typed for "แ", the commonest slip.
  const fixed = key(typed);
  if (provinces.includes(fixed)) return fixed;
  const best = provinces.map((p) => ({ p, d: distance(fixed, p) })).sort((a, b) => a.d - b.d)[0];
  return best && best.d <= Math.max(1, Math.floor([...fixed].length / 4)) ? best.p : null;
}
